import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { titleCase } from 'title-case';
import { formsApi } from '../../../../../../api/forms';
import { showErrorMessage } from '../../../../../../components/base/Notifications';
import {
  selectBackgroundColor,
  selectBackgroundImageName,
  selectBackgroundImageUrl,
  selectBackgroundPosition,
  selectBackgroundSize,
  selectButtonColor,
  selectButtonMinWidth,
  selectButtonTextColor,
  selectFieldBackgroundColor,
  selectFieldBorderColor,
  selectFieldLightBorders,
  selectFieldPlaceholderColor,
  selectFieldTextColor,
  selectFormColor,
  selectFormImageName,
  selectFormImageUrl,
  selectFormLinkColor,
  selectFormPosition,
  selectFormSize,
  selectFormTextColor,
  setBackgroundColor,
  setBackgroundImageName,
  setBackgroundImageUrl,
  setBackgroundPosition,
  setBackgroundSize,
  setButtonColor,
  setButtonMinWidth,
  setButtonTextColor,
  setFieldBackgroundColor,
  setFieldBorderColor,
  setFieldLightBorders,
  setFieldPlaceholderColor,
  setFieldTextColor,
  setFormBackgroundImageName,
  setFormBackgroundImageUrl,
  setFormColor,
  setFormLinkColor,
  setFormPosition,
  setFormSize,
  setFormTextColor,
} from '../../../../../../store/reducers/forms';
import ColorScheme from '../../../Components/ColorScheme';
import ColorSelector from '../../../Components/ColorSelector';
import Dropdown from '../../../Components/Dropdown';
import Input from '../../../Components/ModalSettings/Input';
import settingsStyles from '../../../Components/ModalSettings/ModalSettings.module.scss';
import Switch from '../../../Components/ModalSettings/Switch';
import PictureSelector from '../../../Components/PictureSelector';
import ButtonRadius from './ButtonRadius';
import {
  coverPositions,
  getPosition,
  noCoverPositions,
  uncommonPositions,
} from './options';

const StyleSettings = () => {
  const dispatch = useDispatch();
  const backgroundImageRef = useRef(null);
  const formImageRef = useRef(null);
  const uploadingBackgroundImage = useRef(null);
  const formColor = useSelector(selectFormColor);
  const fileFormName = useSelector(selectFormImageName);
  const textColor = useSelector(selectFormTextColor);
  const linkColor = useSelector(selectFormLinkColor);
  const buttonColor = useSelector(selectButtonColor);
  const buttonTextColor = useSelector(selectButtonTextColor);
  const backgroundColor = useSelector(selectBackgroundColor);
  const fileBackgroundName = useSelector(selectBackgroundImageName);
  const backgroundUrl = useSelector(selectBackgroundImageUrl);
  const formUrl = useSelector(selectFormImageUrl);
  const [uploadBackgroundImage, uploadBackgroundResult] =
    formsApi.useUploadImageMutation();
  const [uploadFormImage, uploadFormResult] = formsApi.useUploadImageMutation();
  const backgroundPosition = useSelector(selectBackgroundPosition);
  const backgroundPositionValue = getPosition(backgroundPosition);
  const backgroundSize = useSelector(selectBackgroundSize);
  const backgroundExtend = backgroundSize === 'cover';
  const formPosition = useSelector(selectFormPosition);
  const formPositionValue = getPosition(formPosition);
  const formBackgroundSize = useSelector(selectFormSize);
  const formBackgroundExtend = formBackgroundSize === 'cover';
  const fieldBackgroundColor = useSelector(selectFieldBackgroundColor);
  const placeholderColor = useSelector(selectFieldPlaceholderColor);
  const borderColor = useSelector(selectFieldBorderColor);
  const fieldTextColor = useSelector(selectFieldTextColor);
  const buttonMinWidth = useSelector(selectButtonMinWidth);
  const lightBorders = useSelector(selectFieldLightBorders);
  const [errors, setErrors] = useState({});

  const handleBackgroundFileUpload = async (event) => {
    const file = event.target.files[0];

    if (!file) return;

    if (
      (file.type === 'image/jpeg' ||
        file.type === 'image/png' ||
        file.type === 'image/gif') &&
      (file.name.endsWith('.jpg') ||
        file.name.endsWith('.jpeg') ||
        file.name.endsWith('.png') ||
        file.name.endsWith('.gif'))
    ) {
      uploadingBackgroundImage.current = file;
      const formData = new FormData();
      formData.append('form_image', file);
      await uploadBackgroundImage(formData);
    } else {
      showErrorMessage(
        'Please select a valid image file. JPG, PNG and GIF are supported.'
      );
    }
  };

  useEffect(() => {
    if (
      uploadBackgroundResult.isSuccess &&
      uploadBackgroundResult.data?.form_image
    ) {
      dispatch(
        setBackgroundImageName(uploadingBackgroundImage.current?.name || '')
      );
      dispatch(setBackgroundImageUrl(uploadBackgroundResult.data.form_image));
      uploadingBackgroundImage.current = null;
    } else if (uploadBackgroundResult.isError) {
      if (backgroundImageRef.current) {
        backgroundImageRef.current.reset();
      }
      const message =
        uploadBackgroundResult.error?.data?.form_image &&
        uploadBackgroundResult.error?.data?.form_image.length > 0
          ? uploadBackgroundResult.error?.data?.form_image[0]
          : '';
      if (
        message ===
        'Upload a valid image. The file you uploaded was either not an image or a corrupted image.'
      ) {
        showErrorMessage(
          'The file you uploaded could not be used: it is either not an image or a corrupted one.'
        );
      } else {
        showErrorMessage(
          'An unknown error occurred when processing your file. Please try again.'
        );
      }
    }
  }, [uploadBackgroundResult]);

  const handleFormFileUpload = async (event) => {
    const file = event.target.files[0];

    if (!file) return;

    if (
      (file.type === 'image/jpeg' ||
        file.type === 'image/png' ||
        file.type === 'image/gif') &&
      (file.name.endsWith('.jpg') ||
        file.name.endsWith('.jpeg') ||
        file.name.endsWith('.png') ||
        file.name.endsWith('.gif'))
    ) {
      uploadFormImage.current = file;
      const formData = new FormData();
      formData.append('form_image', file);
      await uploadFormImage(formData);
    } else {
      showErrorMessage(
        'Please select a valid image file. JPG, PNG and GIF are supported.'
      );
    }
  };

  useEffect(() => {
    if (uploadFormResult.isSuccess && uploadFormResult.data?.form_image) {
      dispatch(setFormBackgroundImageName(uploadFormImage.current?.name || ''));
      dispatch(setFormBackgroundImageUrl(uploadFormResult.data.form_image));
      uploadFormImage.current = null;
    } else if (uploadFormResult.isError) {
      if (formImageRef.current) {
        formImageRef.current.reset();
      }
      const message =
        uploadFormResult.error?.data?.form_image &&
        uploadFormResult.error?.data?.form_image.length > 0
          ? uploadFormResult.error?.data?.form_image[0]
          : '';
      if (
        message ===
        'Upload a valid image. The file you uploaded was either not an image or a corrupted image.'
      ) {
        showErrorMessage(
          'The file you uploaded could not be used: it is either not an image or a corrupted one.'
        );
      } else {
        showErrorMessage(
          'An unknown error occurred when processing your file. Please try again.'
        );
      }
    }
  }, [uploadFormResult]);

  const removeBackgroundImage = () => {
    dispatch(setBackgroundImageName(''));
    dispatch(setBackgroundImageUrl(''));
  };

  const removeFormImage = () => {
    dispatch(setFormBackgroundImageName(''));
    dispatch(setFormBackgroundImageUrl(''));
  };

  const handleFormSizeChange = () => {
    const newValue = !formBackgroundExtend;
    dispatch(setFormSize(newValue ? 'cover' : 'initial'));
    if (newValue && uncommonPositions.includes(formPosition)) {
      dispatch(setFormPosition('center'));
    }
  };

  const handleBackgroundSizeChange = () => {
    const newValue = !backgroundExtend;
    dispatch(setBackgroundSize(newValue ? 'cover' : 'initial'));
    if (newValue && uncommonPositions.includes(backgroundPosition)) {
      dispatch(setBackgroundPosition('center'));
    }
  };

  const handleFormPositionChange = (newPos) => {
    dispatch(setFormPosition(newPos.value));
  };

  const handleBackgroundPositionChange = (newPos) => {
    dispatch(setBackgroundPosition(newPos.value));
  };

  return (
    <>
      <div className={settingsStyles.section_title}>{titleCase('Style')}</div>

      <ColorScheme />

      <div className={settingsStyles.section_title}>
        {titleCase('Form style')}
      </div>

      <div className={settingsStyles.row}>
        <ColorSelector
          id="form"
          title="Form Color"
          color={formColor}
          colorChange={(newVal) => dispatch(setFormColor(newVal))}
        />

        <PictureSelector
          ref={formImageRef}
          title="Form Image"
          url={formUrl}
          fileName={fileFormName}
          uploadResult={uploadFormResult}
          handleFileUpload={handleFormFileUpload}
          removeImage={removeFormImage}
        />

        {formUrl ? (
          <>
            <Dropdown
              title="Image Position"
              options={formBackgroundExtend ? coverPositions : noCoverPositions}
              onChange={handleFormPositionChange}
              value={formPositionValue}
            />
            <div className={settingsStyles.extend_switch_wrapper}>
              <Switch
                text="Extend image"
                checked={formBackgroundExtend}
                onChange={handleFormSizeChange}
              />
            </div>
          </>
        ) : null}

        <ColorSelector
          id="text"
          title="Text Color"
          color={textColor}
          colorChange={(newVal) => dispatch(setFormTextColor(newVal))}
        />

        <ColorSelector
          id="link"
          title="Link Color"
          color={linkColor}
          colorChange={(newVal) => dispatch(setFormLinkColor(newVal))}
          right
        />
      </div>

      <div className={settingsStyles.section_title}>
        {titleCase('Button style')}
      </div>

      <div className={settingsStyles.row}>
        <ColorSelector
          id="button"
          title="Button Color"
          color={buttonColor}
          colorChange={(newVal) => dispatch(setButtonColor(newVal))}
        />

        <ColorSelector
          id="buttonText"
          title="Button Text Color"
          color={buttonTextColor}
          colorChange={(newVal) => dispatch(setButtonTextColor(newVal))}
          right
        />

        <div className={settingsStyles.half_row}>
          <Input
            title="Button Min Width"
            value={buttonMinWidth}
            placeholder="Type here"
            suffix="px"
            onChange={(e) => {
              const newVal = e.currentTarget.value;
              if (/^\d*$/.test(newVal)) {
                setErrors({ ...errors, buttonMinWidth: '' });
                dispatch(setButtonMinWidth(newVal));
              } else {
                setErrors({ ...errors, buttonMinWidth: 'Invalid entry' });
              }
            }}
            maxLength={5}
            error={errors.buttonMinWidth}
          />
        </div>

        <div className={settingsStyles.half_row}>
          <ButtonRadius />
        </div>
      </div>

      <div className={settingsStyles.section_title}>
        {titleCase('Background style')}
      </div>

      <div className={settingsStyles.row}>
        <ColorSelector
          id="background"
          title="Background Color"
          color={backgroundColor}
          colorChange={(newVal) => dispatch(setBackgroundColor(newVal))}
        />

        <PictureSelector
          ref={backgroundImageRef}
          title="Background Image"
          url={backgroundUrl}
          fileName={fileBackgroundName}
          uploadResult={uploadBackgroundResult}
          handleFileUpload={handleBackgroundFileUpload}
          removeImage={removeBackgroundImage}
        />

        {backgroundUrl ? (
          <>
            <Dropdown
              title="Image Position"
              options={backgroundExtend ? coverPositions : noCoverPositions}
              onChange={handleBackgroundPositionChange}
              value={backgroundPositionValue}
            />
            <div className={settingsStyles.extend_switch_wrapper}>
              <Switch
                text="Extend image"
                checked={backgroundExtend}
                onChange={handleBackgroundSizeChange}
              />
            </div>
          </>
        ) : null}
      </div>

      <div className={settingsStyles.section_title}>
        {titleCase('Field style')}
      </div>

      <div className={settingsStyles.row}>
        <ColorSelector
          id="fieldBackground"
          title="Background Color"
          color={fieldBackgroundColor}
          colorChange={(newVal) => dispatch(setFieldBackgroundColor(newVal))}
        />

        <ColorSelector
          id="placeholder"
          title="Placeholder Color"
          color={placeholderColor}
          colorChange={(newVal) => dispatch(setFieldPlaceholderColor(newVal))}
          right
        />

        <ColorSelector
          id="border"
          title="Border Color"
          color={borderColor}
          colorChange={(newVal) => dispatch(setFieldBorderColor(newVal))}
        />

        <div className={settingsStyles.extend_switch_wrapper}>
          <Switch
            text="Light Borders"
            checked={lightBorders}
            onChange={() => dispatch(setFieldLightBorders(!lightBorders))}
          />
        </div>

        <ColorSelector
          id="fieldText"
          title="Text Color"
          color={fieldTextColor}
          colorChange={(newVal) => dispatch(setFieldTextColor(newVal))}
        />
      </div>
    </>
  );
};

export default StyleSettings;
